import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { Label } from '@apps/web/components/ui/label';
import { Switch } from '@apps/web/components/ui/switch';
import { type MemoryFieldOption } from '@apps/web/lib/memory_inputs';

type Props = {
    readonly entityLabel: 'Caregiver' | 'Client';
    readonly options: ReadonlyArray<MemoryFieldOption>;
    readonly enabledFields: ReadonlyArray<string>;
    readonly isSaving: boolean;
    readonly onToggle: (name: string, enabled: boolean) => void;
};

export function MemoryInputsSection({
    entityLabel,
    options,
    enabledFields,
    isSaving,
    onToggle,
}: Props) {
    const [open, setOpen] = useState(false);
    const configurable = options.filter((option) => !option.always_on);
    const profileFieldsLabel = `${entityLabel} profile fields`;

    return (
        <Collapsible
            open={open}
            onOpenChange={setOpen}
            className="bg-muted/50 space-y-3 rounded-md border p-3"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                    <Label>{profileFieldsLabel}</Label>
                    <p className="text-muted-foreground text-xs">
                        Which {entityLabel.toLowerCase()} fields go into this
                        organization&rsquo;s memory. Fields its EHR
                        doesn&rsquo;t sync stay empty. Saving changes queues
                        live memory rebuilds for active{' '}
                        {entityLabel.toLowerCase()}.
                    </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <CollapsibleTrigger
                        type="button"
                        className="text-muted-foreground hover:bg-accent hover:text-accent-foreground group flex size-7 items-center justify-center rounded-md transition-colors"
                        aria-label={`${open ? 'Collapse' : 'Expand'} ${profileFieldsLabel.toLowerCase()}`}
                    >
                        <ChevronDown className="size-4 transition-transform group-data-[state=open]:rotate-180" />
                    </CollapsibleTrigger>
                </div>
            </div>
            <CollapsibleContent className="divide-y">
                {configurable.map((option) => (
                    <div
                        key={option.name}
                        className="flex items-center justify-between gap-3 py-2"
                    >
                        <div className="min-w-0">
                            <div className="text-sm">{option.label}</div>
                            <div className="text-muted-foreground text-xs break-words">
                                {option.vendor_label ?? option.name}
                            </div>
                        </div>
                        <Switch
                            checked={enabledFields.includes(option.name)}
                            onCheckedChange={(checked) =>
                                onToggle(option.name, checked)
                            }
                            disabled={isSaving}
                            aria-label={`${entityLabel}: ${option.label}`}
                        />
                    </div>
                ))}
            </CollapsibleContent>
        </Collapsible>
    );
}
